/* eslint-disable @typescript-eslint/no-explicit-any */
// FIXME: fix any
import { isInternalUser } from '@/app/navigation/access';
import { DataTable } from '@/components/ui/data-table/data-table';
import { DataTableConsumer } from '@/components/ui/data-table/types';
import { useCurrentUser } from '@/features/users/users.hooks';
import { useFilterableTable } from '@/hooks/useFilterableTable';
import { usePanelFilterState } from '@/hooks/usePanelFilterState';
import { usePanelSelectionState } from '@/hooks/usePanelSelectionState';
import { usePersistedTableConfig } from '@/hooks/usePersistedTableConfig';
import { ShareContent } from '@/utils/share';
import { Project } from '@certhon/models';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { projectFields } from './project.fields';
import { omit } from 'remeda';
import { completeConfig } from '@/components/ui/data-table/config';
import { projectsTableConfig } from '@/config/table-configs';
import { getProjectActions } from './project.actions';
import { useQueryClient } from '@tanstack/react-query';

export { DataTable } from '@/components/ui/data-table/data-table';
// The list is active-only by API contract and archived projects have their
// own page, so an archived column/filter would only suggest otherwise.
const fields = omit(projectFields, ['archived']);

const excludedColumns: string[] = ['klant_id'];
// Following is an internal concept, and /users only returns the external's
// own row, so the follower badges could not be resolved anyway.
const externalExcludedColumns: string[] = [
  ...excludedColumns,
  'users',
  'following',
];

const emptyArray: any[] = [];

// Following is internal-only. Externals get no default chip: the list is
// active-only by API contract and archived projects have their own page.
const internalDefaultFilter = 'following is: "true"';
const externalDefaultFilter = '';

export function ProjectTable({
  basePath,
  data,
  isLoading,
}: {
  /** Path of the list panel; rows open at `${basePath}/:projectId`. */
  basePath: string;
} & DataTableConsumer<Project>) {
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const internal = isInternalUser(currentUser);
  const defaultFilter = internal
    ? internalDefaultFilter
    : externalDefaultFilter;

  // Get row actions for projects
  const rowActions = useMemo(
    () => getProjectActions(queryClient, currentUser),
    [currentUser, queryClient],
  );

  const { filterValue, setFilterValue } = usePanelFilterState({
    defaultFilter,
  });
  const { rowSelection, setRowSelection, clearSelection } =
    usePanelSelectionState();

  // Clear selection when filter changes (context change)
  const prevFilterRef = useRef(filterValue);
  useEffect(() => {
    if (prevFilterRef.current !== filterValue) {
      clearSelection();
      prevFilterRef.current = filterValue;
    }
  }, [filterValue, clearSelection]);

  // Use schema-driven filter system
  const {
    tokenDefinitions,
    filteredData,
    columns,
    isFiltering,
    resultCount,
    totalCount,
  } = useFilterableTable<Project>({
    fields,
    data: data || emptyArray,
    filterValue,
    excludeColumns: internal ? excludedColumns : externalExcludedColumns,
    context: currentUser ? { userId: currentUser.recnum } : undefined, // for following filter
  });

  const completeDefaultConfig = useMemo(
    () => completeConfig(projectsTableConfig, columns),
    [columns],
  );

  const [config, setConfig] = usePersistedTableConfig(
    'projects',
    completeDefaultConfig,
  );

  const createUriPath = useCallback(
    (p: Project): string => `${basePath}/${p.recnum}`,
    [basePath],
  );

  const createShareContent = useCallback(
    (project: Project, url: string): ShareContent => ({
      title: `Project: ${project.projectname}`,
      text: `${project.klant_naam} - ${project.projectname}`,
      url,
    }),
    [],
  );

  return (
    <DataTable<Project, any>
      isLoading={isLoading}
      data={filteredData}
      columns={columns}
      config={config}
      onConfigChange={setConfig}
      createUriPath={createUriPath}
      createShareContent={createShareContent}
      filterValue={filterValue}
      onFilterChange={setFilterValue}
      tokenDefinitions={tokenDefinitions}
      defaultFilter={defaultFilter}
      isFiltering={isFiltering}
      resultCount={resultCount}
      totalCount={totalCount}
      entityName="projects"
      addDisabled
      actions={rowActions}
      rowSelection={rowSelection}
      onRowSelectionChange={setRowSelection}
    />
  );
}

export default ProjectTable;
